import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

import { Paper, Grid, Select, MenuItem, Box } from '@mui/material';
import GiftVoucherGrid from './components/GiftVoucherGrid';
import GiftVoucherDrawer from './components/GiftVoucherDrawer';

import { SCUIButton, SCUILoadingLinear } from '../../../../atomic/atoms';
import { GiftVoucherQuery } from '../../../../services/query';
import { IGiftVoucherData, sortingOptions, sortValues } from './enum';
import { GV } from './giftVouchers.classes';

export function GiftVouchers({ customerData }: any) {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [gridData, setGridData] = useState<any[]>([]);
    const [selectedGift, setSelectedGift] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [voucherType, setVoucherType] = useState<number>(1);
    const [startDate, setStartDate] = useState<any>();
    const [endDate, setEndDate] = useState<any>();
    const [sortingKey, setSortingKey] = useState<any>(sortingOptions[0].sortKey);
    const [sortingValue, setSortingValue] = useState<any>(sortValues[0].id);

    const regionId: string = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId?.toString();
    const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();

    const languages = JSON.parse(import.meta.env.VITE_LANGUAGE_LIST!).filter((x: any) =>
        x.countries.includes(countryId)
    );

    const { control, handleSubmit, reset } = useForm<IGiftVoucherData>({
        defaultValues: {
            id: '',
            status: '',
            value: '',
            minAmount: '',
            maxAmount: '',
            description: '',
            couponSource: '',
            ...languages.reduce(
                (acc: Record<string, any>, lang: any) => ({
                    ...acc,
                    [`language-${lang.languageId}`]: '',
                }),
                {}
            ),
        },
    });

    const { t } = useTranslation();

    const getGiftVouchers = useCallback(() => {
        setIsLoading(true);
        GiftVoucherQuery.GetCustomerGiftVouchers({
            regionId,
            customerId: customerData?.customer?.customerId,
            skipCount: page,
            maxResultCount: rowsPerPage,
            sortPropertyName: sortingKey,
            sortValue: sortingValue,
        })
            .then((res: any) => {
                setIsLoading(false);
                clearSelected();
                setGridData(res.data?.items || []);
                setTotalCount(res.data?.totalCount || 0);
            })
            .catch(() => {
                setIsLoading(false);
                clearSelected();
                Swal.fire(t('general.error'), '', 'error');
            });
    }, [customerData?.customer?.customerId, page, regionId, rowsPerPage, t, sortingKey, sortingValue]);

    const clearSelected = () => {
        reset({
            id: '',
            status: '',
            value: '',
            minAmount: '',
            maxAmount: '',
            description: '',
            couponSource: '',
            ...languages.reduce(
                (acc: Record<string, any>, lang: any) => ({
                    ...acc,
                    [`language-${lang.languageId}`]: '',
                }),
                {}
            ),
        });
        setEndDate(null);
        setStartDate(null);
        setVoucherType(1);
        setIsDrawerOpen(false);
        setSelectedGift(null);
    };

    useEffect(() => {
        getGiftVouchers();
    }, [page, rowsPerPage, sortingKey, sortingValue]);

    return (
        <>
            <Paper sx={{ padding: '16px' }} className={GV.page}>
                {isLoading && (
                    <Grid
                        size={{
                            xl: 12,
                            md: 12,
                            lg: 12,
                            xs: 12,
                        }}
                    >
                        <SCUILoadingLinear display={isLoading} />
                    </Grid>
                )}
                <SCUIButton
                    className={GV.inputButtonAdd}
                    text={t('giftVoucher.addGiftVoucher')}
                    type="primary"
                    sizeType="default"
                    size={'s'}
                    onClick={() => {
                        setIsDrawerOpen(true);
                    }}
                />
                <Box
                    className={GV.sortBar}
                    sx={{ display: 'flex', flexDirection: 'row', marginTop: '16px', marginBottom: '8px' }}
                >
                    <Select
                        className={GV.inputSelectSortKey}
                        onChange={(e) => setSortingKey(e.target.value)}
                        value={sortingKey}
                    >
                        {sortingOptions.map((sortItem: any) => (
                            <MenuItem key={sortItem.sortKey} value={sortItem.sortKey}>
                                {t(sortItem.name)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        className={GV.inputSelectSortDirection}
                        onChange={(e) => setSortingValue(e.target.value)}
                        sx={{ marginLeft: '16px' }}
                        value={sortingValue}
                    >
                        {sortValues.map((sortItem: any) => (
                            <MenuItem key={sortItem.id} value={sortItem.id}>
                                {t(sortItem.name)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <GiftVoucherGrid
                    setPage={setPage}
                    gridData={gridData}
                    setRowsPerPage={setRowsPerPage}
                    setIsDrawerOpen={setIsDrawerOpen}
                    setIsLoading={setIsLoading}
                    voucherType={voucherType}
                    reset={reset}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    getGiftVouchers={getGiftVouchers}
                    setVoucherType={setVoucherType}
                    setSelectedGift={setSelectedGift}
                    totalCount={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                />
                <GiftVoucherDrawer
                    clearSelected={clearSelected}
                    isDrawerOpen={isDrawerOpen}
                    setStartDate={setStartDate}
                    startDate={startDate}
                    setEndDate={setEndDate}
                    endDate={endDate}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                    selectedGift={selectedGift}
                    customerData={customerData}
                    voucherType={voucherType}
                    getGiftVouchers={getGiftVouchers}
                    control={control}
                    handleSubmit={handleSubmit}
                    setVoucherType={setVoucherType}
                />
            </Paper>
        </>
    );
}
